// 詳細パネル v2 の「住所」ブロック（段階3〜5・中止でない）。design/handoff-README.md §3 ④-4。
// 状態ピル・フォームURL・宛先の要約・出荷指示の主ボタン（住所入力済のときだけ赤い大きなボタン）。
import { ADDR_NUDGE_AFTER_DAYS, fmtMD, shipErrorText } from "../../../../lib/giftingRules";
import type { GfActionKey, GfDealDetail, GfEntry } from "../../../../types/gifting";
import { FormUrlBox } from "./FormUrlBox";
import { agoLabel, calDaysAgo } from "./dayLabel";

function fmtPostal(s: string | null): string {
  const d = (s ?? "").replace(/\D/g, "");
  return d.length === 7 ? `〒${d.slice(0, 3)}-${d.slice(3)}` : s ? `〒${s}` : "";
}

export function AddressBlock({
  entry,
  detail,
  nowIso,
  actionKey,
  busy,
  onShip,
  onProxy,
  changed,
  toast,
}: {
  entry: GfEntry;
  detail: GfDealDetail | null;
  nowIso: string;
  actionKey: GfActionKey;
  busy: boolean;
  /** 「出荷指示を送る」／「住所を修正」（EntryPanel の onMain＝ship モーダル／発送タブ誘導） */
  onShip: () => void;
  /** 代理入力（発送タブへ） */
  onProxy: () => void;
  changed: () => Promise<void>;
  toast: (m: string) => void;
}) {
  const received = entry.address_state === "received";
  const ordered = !!entry.ship_order_no;
  const needShip = entry.stage === 4 && entry.status === "address_received" && !ordered;
  const invalid = !!entry.ship_validation && !entry.ship_validation.ok;
  const waitDays = calDaysAgo(entry.stage_changed_at, nowIso) ?? 0;

  let pill: { cls: string; label: string };
  if (ordered) pill = { cls: "is-done", label: "出荷指示済" };
  else if (received && invalid) pill = { cls: "is-error", label: "書式エラー" };
  else if (received) pill = { cls: "is-recv", label: "入力済・出荷未指示" };
  else if (entry.address_state === "form_sent") {
    pill = { cls: waitDays >= ADDR_NUDGE_AFTER_DAYS ? "is-late" : "is-wait", label: `入力待ち ${waitDays}日` };
  } else pill = { cls: "is-none", label: "未送付" };

  const subAgo = detail?.address_submitted_at ? agoLabel(calDaysAgo(detail.address_submitted_at, nowIso)) : "";
  const via = entry.address_source === "proxy" ? "代理入力" : "本人フォームで入力";

  return (
    <section className={`gf-ab${needShip ? " is-ship" : ""}`}>
      <div className="gf-ab-head">
        <span className="gf-ab-title">住所</span>
        <span className={`gf-ab-pill ${pill.cls}`}>{pill.label}</span>
        {!received && actionKey === "addrnudge" && <span className="gf-ab-hint">催促のタイミングです</span>}
      </div>

      {detail && !ordered && <FormUrlBox detail={detail} onChanged={changed} toast={toast} />}

      {received && detail && (
        <div className="gf-ab-addr">
          <div className="gf-ab-addr-line">
            <span className="gf-mono">{fmtPostal(detail.ship_postal)}</span> {detail.ship_address}
            {detail.ship_name && <span className="gf-ab-addr-name">{detail.ship_name} 様</span>}
          </div>
          {detail.ship_phone && <div className="gf-ab-addr-sub gf-mono">{detail.ship_phone}</div>}
          <div className="gf-ab-addr-sub">
            {via}
            {subAgo ? ` · ${subAgo}` : ""}
          </div>
        </div>
      )}
      {!received && entry.address_state === "form_sent" && (
        <div className="gf-ab-addr-sub">
          フォームURLを送付済み。入力を待っています。
          <button type="button" className="gf-link-btn" onClick={onProxy}>
            代理入力
          </button>
        </div>
      )}

      {received && invalid && entry.ship_validation && (
        <div className="gf-ab-error">出荷チェック: {shipErrorText(entry.ship_validation.errors)}</div>
      )}

      {needShip && (
        <button type="button" className="gf-ab-main" disabled={busy} onClick={onShip}>
          {invalid ? "住所を修正" : "出荷指示を送る"}
        </button>
      )}
      {ordered && (
        <div className="gf-ab-order">
          受注番号 <b className="gf-mono">{entry.ship_order_no}</b>
          {entry.ship_tracking_no
            ? ` · 追跡 ${entry.ship_carrier ?? ""} ${entry.ship_tracking_no}${entry.shipped_at ? `（発送 ${fmtMD(entry.shipped_at)}）` : ""}`
            : " · 追跡待ち"}
        </div>
      )}
    </section>
  );
}
